/**
 * Toast 工具函数
 * 提供适应页面风格的 toast 提示功能
 */
import toast from 'react-hot-toast';

import i18n from 'i18next';

/**
 * 页面风格的 toast 样式配置
 */
const pageStyleToastOptions = {
  style: {
    background: 'rgba(0, 0, 0, 0.9)',
    color: '#fff',
    border: '1px solid #374151',
    borderRadius: '0.75rem',
    fontSize: '14px',
    fontWeight: '500',
    padding: '12px 20px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },
  duration: 3000,
};

/**
 * 成功样式的 toast 配置
 */
const successToastOptions = {
  ...pageStyleToastOptions,
  iconTheme: {
    primary: '#2dd4bf',
    secondary: '#000',
  },
};

/**
 * 错误样式的 toast 配置
 */
const errorToastOptions = {
  ...pageStyleToastOptions,
  iconTheme: {
    primary: '#ef4444',
    secondary: '#000',
  },
};

/**
 * 显示成功的 toast 提示
 * @param message 提示消息
 * @param options 可选的配置项
 */
export function showSuccessToast(message: string, options?: Partial<typeof successToastOptions>) {
  return toast.success(message, {
    ...successToastOptions,
    ...options,
  });
}

/**
 * 显示错误的 toast 提示
 * @param message 提示消息
 * @param options 可选的配置项
 */
export function showErrorToast(message: string, options?: Partial<typeof errorToastOptions>) {
  return toast.error(message, {
    ...errorToastOptions,
    ...options,
  });
}

/**
 * 显示下载开始的 toast 提示
 * @param chipType 芯片类型 (Apple Chip, Intel Chip, Windows)
 * @param version 版本号
 * @param options 可选的配置项
 */
export function showDownloadStartToast(
  chipType: string,
  version: string,
  options?: Partial<typeof successToastOptions>,
) {
  const message = i18n.t('toast.downloadStart', { chipType, version });
  return showSuccessToast(message, options);
}

/**
 * 显示下载失败的 toast 提示
 * @param reason 失败原因（可选）
 * @param options 可选的配置项
 */
export function showDownloadErrorToast(
  reason?: string,
  options?: Partial<typeof errorToastOptions>,
) {
  const message = reason || i18n.t('toast.downloadFailed');
  return showErrorToast(message, options);
}

/**
 * 显示正在打开下载页面的 toast 提示
 * @param options 可选的配置项
 */
export function showOpeningDownloadPageToast(options?: Partial<typeof successToastOptions>) {
  return showSuccessToast(i18n.t('toast.openingDownloadPage'), {
    ...options,
    duration: 2000,
  });
}

/**
 * 显示弹窗被阻止的 toast 提示
 * @param options 可选的配置项
 */
export function showPopupBlockedToast(options?: Partial<typeof errorToastOptions>) {
  return showErrorToast(i18n.t('toast.popupBlocked'), options);
}

/**
 * 显示下载链接不可用的 toast 提示
 * @param options 可选的配置项
 */
export function showDownloadUnavailableToast(options?: Partial<typeof errorToastOptions>) {
  return showErrorToast(i18n.t('toast.downloadUnavailable'), options);
}

/**
 * 显示无合适版本的 toast 提示，即将跳转到下载页面
 * @param options 可选的配置项
 */
export function showNoSuitableVersionToast(options?: Partial<typeof errorToastOptions>) {
  return showErrorToast(i18n.t('toast.noSuitableVersion'), {
    ...options,
    duration: 2500,
  });
}

/**
 * 显示版本不匹配的警告 toast 提示
 * @param currentSystem 当前系统名称
 * @param downloadSystem 下载版本的系统名称
 * @param options 可选的配置项
 */
export function showVersionMismatchToast(
  currentSystem: string,
  downloadSystem: string,
  options?: Partial<typeof errorToastOptions>,
) {
  const message = i18n.t('toast.versionMismatch', { currentSystem, downloadSystem });
  return toast(message, {
    ...errorToastOptions,
    ...options,
    duration: 5000, // 警告提示显示时间稍长一些
  });
}
